import { useState, useEffect, useCallback, useMemo } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig.js'; 
import { BroadcasterModule } from './broadcasterModule.jsx';
import { RuleEditor } from './RuleEditor.jsx';
import { BroadcasterCreator } from './BroadcasterCreator.jsx';
import { RegulationTextCreator } from './RegulationTextCreator.jsx'; // <--- IMPORT
import { useRulesData } from '../hooks/useRulesData.js'; 
import './Editor.css';

export const Editor = () => {
    // Note: ensure 'refreshData' is destructured if you want to reload lists after edits, 
    // though local state updates work for immediate feedback.
    const { allRulesData, broadcasters, regulationTexts, loading, refreshData } = useRulesData();
    
    const [localRulesData, setLocalRulesData] = useState([]);
    const [localTextsData, setLocalTextsData] = useState([]); // NEW: Local state for texts
    
    const [selectedBroadcasterId, setSelectedBroadcasterId] = useState('');
    const [selectedRule, setSelectedRule] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [interactionMode, setInteractionMode] = useState('normal');
    
    // UI MODES
    // 'create_broadcaster', 'edit_broadcaster', 'create_text', 'edit_text', or null
    const [formMode, setFormMode] = useState(null); 
    
    const [editingBroadcasterData, setEditingBroadcasterData] = useState(null);
    const [editingTextData, setEditingTextData] = useState(null); // NEW

    useEffect(() => { setLocalRulesData(allRulesData); }, [allRulesData]);
    useEffect(() => { setLocalTextsData(regulationTexts); }, [regulationTexts]); // Sync texts
    
    useEffect(() => { 
        if (broadcasters.length > 0 && !selectedBroadcasterId) {
            setSelectedBroadcasterId(broadcasters[0].broadcaster_id); 
        }
    }, [broadcasters, selectedBroadcasterId]);

    const currentBroadcasterRules = useMemo(() => {
        if (!selectedBroadcasterId || localRulesData.length === 0) return [];
        const broadcaster = localRulesData.find(b => b.broadcaster_id === selectedBroadcasterId);
        if (!broadcaster || !broadcaster.rules) return [];
        return [
            ...(broadcaster.rules.financingObligations || []),
            ...(broadcaster.rules.chronoWindows || []),
            ...(broadcaster.rules.broadcastObligations || [])
        ];
    }, [selectedBroadcasterId, localRulesData]);

    const handleBroadcasterIdChange = (newId) => {
        setSelectedBroadcasterId(newId);
        setSelectedRule(null);
        setInteractionMode('normal');
        setFormMode(null);
    };

    const handleRuleClick = useCallback((clickedRule) => {
        if (interactionMode === 'normal') { 
            setSelectedRule(clickedRule); 
            setFormMode(null);
        } 
        else if (interactionMode === 'pickingParent') {
            setSelectedRule((prev) => ({ ...prev, parent_id: clickedRule.id }));
            setInteractionMode('normal');
        } 
        else if (interactionMode === 'pickingStack') {
            let newStackId = clickedRule.stack_id;
            let needsLocalUpdate = false;
            if (!newStackId) { newStackId = Date.now(); needsLocalUpdate = true; }
            
            if (needsLocalUpdate) {
                const broadcasterIndex = localRulesData.findIndex(b => b.broadcaster_id === selectedBroadcasterId);
                if (broadcasterIndex !== -1) {
                    const broadcaster = { ...localRulesData[broadcasterIndex] };
                    const updateRuleInArray = (ruleArray) => {
                        if (!ruleArray) return [];
                        const idx = ruleArray.findIndex(r => r.id === clickedRule.id);
                        if (idx !== -1) {
                            const newArray = [...ruleArray];
                            newArray[idx] = { ...newArray[idx], stack_id: newStackId };
                            return newArray;
                        }
                        return ruleArray;
                    };
                    broadcaster.rules.financingObligations = updateRuleInArray(broadcaster.rules.financingObligations);
                    const newAllRulesData = [...localRulesData];
                    newAllRulesData[broadcasterIndex] = broadcaster;
                    setLocalRulesData(newAllRulesData);
                }
            }
            setSelectedRule((prev) => ({ ...prev, stack_id: newStackId }));
            setInteractionMode('normal');
        }
    }, [interactionMode, localRulesData, selectedBroadcasterId]);

    // ... [handleSaveRule and handleDeleteRule remain unchanged] ...
    const handleSaveRule = async (updatedRule) => {
        setIsSaving(true);
        try {
            const broadcasterIndex = localRulesData.findIndex(b => b.broadcaster_id === selectedBroadcasterId);
            if (broadcasterIndex === -1) throw new Error("Broadcaster not found");

            const broadcaster = localRulesData[broadcasterIndex];
            if (!broadcaster.firestoreId) throw new Error("Missing Firestore ID");

            let targetField = "";
            let currentRules = [];

            switch (updatedRule.family) {
                case "Obligations de financement":
                    targetField = "rules.financingObligations";
                    currentRules = [...(broadcaster.rules.financingObligations || [])];
                    break;
                case "Chronologie des médias":
                    targetField = "rules.chronoWindows";
                    currentRules = [...(broadcaster.rules.chronoWindows || [])];
                    break;
                case "Obligations de diffusion":
                    targetField = "rules.broadcastObligations";
                    currentRules = [...(broadcaster.rules.broadcastObligations || [])];
                    break;
                default:
                    if (broadcaster.rules.financingObligations?.some(r => r.id === updatedRule.id)) {
                        targetField = "rules.financingObligations";
                        currentRules = [...broadcaster.rules.financingObligations];
                    } else if (broadcaster.rules.chronoWindows?.some(r => r.id === updatedRule.id)) {
                        targetField = "rules.chronoWindows";
                        currentRules = [...broadcaster.rules.chronoWindows];
                    } else if (broadcaster.rules.broadcastObligations?.some(r => r.id === updatedRule.id)) {
                        targetField = "rules.broadcastObligations";
                        currentRules = [...broadcaster.rules.broadcastObligations];
                    } else {
                        targetField = "rules.financingObligations";
                        currentRules = [...(broadcaster.rules.financingObligations || [])];
                        updatedRule.family = "Obligations de financement"; 
                    }
            }

            const ruleIndex = currentRules.findIndex(r => r.id === updatedRule.id);
            if (ruleIndex !== -1) {
                currentRules[ruleIndex] = updatedRule;
            } else {
                currentRules.push(updatedRule);
            }

            const broadcasterRef = doc(db, "broadcasters", broadcaster.firestoreId);
            await updateDoc(broadcasterRef, {
                [targetField]: currentRules
            });

            const updatedBroadcaster = { ...broadcaster };
            const key = targetField.split('.')[1]; 
            updatedBroadcaster.rules[key] = currentRules;

            const newAllRulesData = [...localRulesData];
            newAllRulesData[broadcasterIndex] = updatedBroadcaster;
            setLocalRulesData(newAllRulesData);
            
            setSelectedRule(updatedRule);
            alert("Sauvegardé avec succès !");

        } catch (error) {
            console.error("Error saving rule:", error);
            alert("Erreur lors de la sauvegarde: " + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteRule = async (ruleId) => {
        setIsSaving(true);
        try {
            const broadcasterIndex = localRulesData.findIndex(b => b.broadcaster_id === selectedBroadcasterId);
            if (broadcasterIndex === -1) throw new Error("Broadcaster not found");

            const broadcaster = localRulesData[broadcasterIndex];
            
            let targetField = "";
            let currentRules = [];
            
            if (broadcaster.rules.financingObligations?.some(r => r.id === ruleId)) {
                targetField = "rules.financingObligations";
                currentRules = broadcaster.rules.financingObligations;
            } else if (broadcaster.rules.chronoWindows?.some(r => r.id === ruleId)) {
                targetField = "rules.chronoWindows";
                currentRules = broadcaster.rules.chronoWindows;
            } else if (broadcaster.rules.broadcastObligations?.some(r => r.id === ruleId)) {
                targetField = "rules.broadcastObligations";
                currentRules = broadcaster.rules.broadcastObligations;
            }

            if (!targetField) return;

            const newRules = currentRules.filter(r => r.id !== ruleId);
            const broadcasterRef = doc(db, "broadcasters", broadcaster.firestoreId);
            await updateDoc(broadcasterRef, { [targetField]: newRules });

            const updatedBroadcaster = { ...broadcaster };
            const key = targetField.split('.')[1];
            updatedBroadcaster.rules[key] = newRules;
            
            const newAllRulesData = [...localRulesData];
            newAllRulesData[broadcasterIndex] = updatedBroadcaster;
            setLocalRulesData(newAllRulesData);
            setSelectedRule(null);
            alert("Règle supprimée.");

        } catch (error) {
            alert("Erreur: " + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    // --- Broadcaster Handlers ---
    const handleCreateBroadcasterClick = () => {
        setEditingBroadcasterData(null);
        setFormMode('create_broadcaster');
        setSelectedRule(null);
    };

    const handleEditBroadcasterSelect = (broadcasterId) => {
        const broadcaster = localRulesData.find(b => b.broadcaster_id === broadcasterId);
        if (broadcaster) {
            setEditingBroadcasterData(broadcaster);
            setFormMode('edit_broadcaster');
            setSelectedRule(null);
            setSelectedBroadcasterId(broadcasterId);
        }
    };

    const handleSaveBroadcaster = (savedBroadcaster) => {
        if (formMode === 'create_broadcaster') {
            setLocalRulesData([...localRulesData, savedBroadcaster]);
            setSelectedBroadcasterId(savedBroadcaster.broadcaster_id);
            alert("Diffuseur créé avec succès !");
        } else if (formMode === 'edit_broadcaster') {
            const idx = localRulesData.findIndex(b => b.broadcaster_id === savedBroadcaster.broadcaster_id);
            if (idx !== -1) {
                const newData = [...localRulesData];
                newData[idx] = savedBroadcaster;
                setLocalRulesData(newData);
                alert("Diffuseur modifié avec succès !");
            }
        }
        setFormMode(null);
    };

    const handleDeleteBroadcaster = (deletedId) => {
        const newData = localRulesData.filter(b => b.broadcaster_id !== deletedId);
        setLocalRulesData(newData);
        if (selectedBroadcasterId === deletedId) {
            setSelectedBroadcasterId(newData.length > 0 ? newData[0].broadcaster_id : '');
        }
        setFormMode(null);
        alert("Diffuseur supprimé.");
    };

    // --- Regulation Text Handlers (NEW) ---
    const handleCreateTextClick = () => {
        setEditingTextData(null);
        setFormMode('create_text');
        setSelectedRule(null);
    };

    const handleEditTextSelect = (textId) => {
        // textId from value is string, convert to number if needed or compare flexibly
        const text = localTextsData.find(t => t.text_id === textId);
        if (text) {
            setEditingTextData(text);
            setFormMode('edit_text');
            setSelectedRule(null);
        }
    };

    const handleSaveText = (savedText) => {
        let newTexts = [...localTextsData];
        const idx = newTexts.findIndex(t => t.text_id === savedText.text_id);
        
        if (idx !== -1) {
            newTexts[idx] = savedText; // Update
        } else {
            newTexts.push(savedText); // Create
            // Sort alphabetically by name for convenience
            newTexts.sort((a, b) => (a.text_name || '').localeCompare(b.text_name || ''));
        }
        
        setLocalTextsData(newTexts);
        setFormMode(null);
        if (refreshData) refreshData(); // Optional: trigger full refresh to be safe
        alert("Texte sauvegardé !");
    };

    const handleDeleteText = (deletedId) => {
        const newTexts = localTextsData.filter(t => t.text_id !== deletedId);
        setLocalTextsData(newTexts);
        setFormMode(null);
        alert("Texte supprimé.");
    };


    const handleCreateRule = (family) => {
        const newId = Math.floor(Math.random() * 1000000);
        const newRule = {
            id: newId,
            family: family, 
            rule: "Nouvelle règle",
            rule_type: "Type",
            value_info: { value_unit: "", value_yearly: 0, value_years: [] },
            parent_id: null,
            stack_id: null
        };
        if (family === "Chronologie des médias") {
            newRule.rule_type = "Fenêtre d'exploitation";
            newRule.value_info = { value_unit: "mois", value_yearly: 0 };
        } else if (family === "Obligations de diffusion") {
            newRule.rule_type = "Part catalogue";
            newRule.value_yearly = "Texte par défaut"; 
        }

        setSelectedRule(newRule);
        setFormMode(null);
        setInteractionMode('normal');
    };

    if (loading) return <div>Chargement de l'éditeur...</div>;

    return (
        <div className="editor-container">
            <div className="editor-header">
                <h2>Infobligations - Admin</h2>
                <div className="editor-controls">
                    {/* Header buttons removed in favor of sidebar controls */}
                </div>
            </div>

            <div className="editor-body">
                <div className="editor-preview-pane">
                    {selectedBroadcasterId && (
                        <BroadcasterModule
                            allRulesData={localRulesData}
                            broadcasters={localRulesData.map(b => ({broadcaster_id: b.broadcaster_id, broadcaster_name: b.broadcaster_name}))}
                            zoomScale={1}
                            initialBroadcasterId={selectedBroadcasterId}
                            onBroadcasterChange={handleBroadcasterIdChange}
                            onRuleClick={handleRuleClick}
                            selectedRuleId={selectedRule?.id}
                            parentRuleId={selectedRule?.parent_id}
                            isPickingParent={interactionMode === 'pickingParent'}
                        />
                    )}
                </div>
                <div className="editor-form-pane">
                    
                    {/* LOGIC TO RENDER THE CORRECT FORM */}
                    {(() => {
                        if (formMode === 'create_broadcaster' || formMode === 'edit_broadcaster') {
                            return <BroadcasterCreator 
                                initialData={editingBroadcasterData}
                                onSave={handleSaveBroadcaster}
                                onCancel={() => setFormMode(null)}
                                onDelete={handleDeleteBroadcaster}
                            />
                        }
                        if (formMode === 'create_text' || formMode === 'edit_text') {
                            return <RegulationTextCreator 
                                initialData={editingTextData}
                                onSave={handleSaveText}
                                onCancel={() => setFormMode(null)}
                                onDelete={handleDeleteText}
                            />
                        }
                        return (
                            <>
                                {/* HIDE TOOLBAR IF A RULE IS SELECTED */}
                                {!selectedRule && (
                                    <div className="form-toolbar">
                                        {/* --- RULES SECTION --- */}
                                        <div className="toolbar-section">
                                            <select 
                                                id="create-rule-select"
                                                className="create-rule-select" 
                                                onChange={(e) => { if (e.target.value) { handleCreateRule(e.target.value); e.target.value = ""; } }} 
                                                defaultValue=""
                                                disabled={!selectedBroadcasterId}
                                            >
                                                <option value="" disabled>+ Créer une règle...</option>
                                                <option value="Obligations de financement">Obligation de financement</option>
                                                <option value="Obligations de diffusion">Obligation de diffusion</option>
                                                <option value="Chronologie des médias">Chronologie des médias</option>
                                            </select>
                                        </div>

                                        {/* --- BROADCASTERS SECTION --- */}
                                        <div className="toolbar-section">
                                            <select 
                                                id="edit-broadcaster-select"
                                                className="edit-select" 
                                                onChange={(e) => { if (e.target.value) { handleEditBroadcasterSelect(e.target.value); e.target.value = ""; } }} 
                                                defaultValue=""
                                            >
                                                <option value="" disabled>Modifier un diffuseur...</option>
                                                {localRulesData.map(b => (
                                                    <option key={b.broadcaster_id} value={b.broadcaster_id}>
                                                        {b.broadcaster_name}
                                                    </option>
                                                ))}
                                            </select>
                                            <button 
                                                className="create-btn primary"
                                                onClick={handleCreateBroadcasterClick}
                                            >
                                                + Nouveau Diffuseur
                                            </button>
                                        </div>

                                        {/* --- REGULATION TEXTS SECTION (NEW) --- */}
                                        <div className="toolbar-section">
                                            <select 
                                                id="edit-text-select"
                                                className="edit-select" 
                                                onChange={(e) => { if (e.target.value) { handleEditTextSelect(e.target.value); e.target.value = ""; } }} 
                                                defaultValue=""
                                            >
                                                <option value="" disabled>Modifier un texte...</option>
                                                {localTextsData.map(t => (
                                                    <option key={t.text_id} value={t.text_id}>
                                                        {t.text_name}
                                                    </option>
                                                ))}
                                            </select>
                                            <button 
                                                className="create-btn secondary"
                                                onClick={handleCreateTextClick}
                                            >
                                                + Nouveau Texte
                                            </button>
                                        </div>
                                    </div>
                                )}
                                
                                <RuleEditor 
                                    ruleData={selectedRule}
                                    broadcasterRules={currentBroadcasterRules} 
                                    regulationTexts={localTextsData} // Use local texts data
                                    onSave={handleSaveRule} 
                                    onCancel={() => setSelectedRule(null)}
                                    onDelete={handleDeleteRule} 
                                    onStartPickParent={() => setInteractionMode(interactionMode === 'pickingParent' ? 'normal' : 'pickingParent')}
                                    onStartPickStack={() => setInteractionMode(interactionMode === 'pickingStack' ? 'normal' : 'pickingStack')}
                                    isPickingParent={interactionMode === 'pickingParent'}
                                    isPickingStack={interactionMode === 'pickingStack'}
                                />
                                {interactionMode === 'pickingParent' && <div className="picking-indicator">Cliquez sur une règle pour la définir comme parent.</div>}
                                {interactionMode === 'pickingStack' && <div className="picking-indicator">Cliquez sur une règle pour joindre sa pile.</div>}
                            </>
                        );
                    })()}

                    {isSaving && <div className="saving-indicator">Sauvegarde en cours...</div>}
                </div>
            </div>
        </div>
    );
};